/**
 * purchase.salefish.app — where buyers and partners open what SaleFish sends
 * them (Andrew, 2026-10-26: "Add the PropPay style signing page. create a new
 * portal called purchase.salefish.app"). It is the same deployment as
 * Console, answered by host: on that host only the pages a buyer, broker or
 * solicitor is sent to are served, and anything else goes to Console.
 *
 *   /sign/…                signing an agreement or a change
 *   /id/…                  scanning an ID from the rep's QR code
 *   /confirm-email/…       confirming an address before the agreement goes out
 *   /review/…              the builder's solicitor reviewing an envelope
 *   /worksheet-response/…  a broker answering a worksheet decision
 *   /documents             what someone who types the address in sees
 *
 * The emails carry this host only once PURCHASE_URL is set on the deployment
 * (server/services/contracts/signing.ts `buyerBase`), which is done after the
 * domain answers — so no link is ever sent to a host that isn't serving yet.
 */

export const BUYER_PATH_PREFIXES = ["/sign/", "/id/", "/confirm-email/", "/review/", "/worksheet-response/"] as const;

/** The landing page for someone who opens the host itself. */
export const PURCHASE_HOME_PATH = "/documents";

export const isBuyerPath = (path: string) => path === PURCHASE_HOME_PATH || BUYER_PATH_PREFIXES.some((p) => path.startsWith(p));

/** purchase.salefish.app, or purchase.<any domain> on a preview of the same setup. */
export const isPurchaseHost = (hostname: string) => /^purchase\./i.test(hostname);

/** Console's address beside the purchase host: purchase.salefish.app → console.salefish.app. */
export const consoleOriginFor = (origin: string) => origin.replace(/\/\/purchase\./i, "//console.");

/**
 * The purchase host named by a deployment's PURCHASE_URL ("purchase.salefish.app"),
 * or null when it names none — the "Track your purchase at …" line in buyer
 * emails is left out until the host answers (shared/purchasePortal.ts).
 */
export function purchaseHostOf(url: string | null | undefined): string | null {
  try {
    const raw = (url ?? "").trim();
    if (!raw) return null;
    const host = new URL(raw).hostname;
    return isPurchaseHost(host) ? host : null;
  } catch {
    return null;
  }
}
